// An acknowledgment belongs to one resource and one submitted snapshot.
export class DocumentSaveSession {
  constructor(onChange = () => {}) {
    this.resources = new Map()
    this.operations = new Set()
    this.onChange = onChange
    this.revision = 0
    this.loaded = 0
    this.total = 0
  }

  register(key, resource) {
    const previous = this.resources.get(key)
    const row = previous || { acknowledged: resource.snapshot(), version: 0, dirty: false, pending: null, error: false }
    Object.assign(row, resource, { key })
    this.resources.set(key, row)
    this.notify()
    return row
  }

  changed(key) {
    const row = this.resources.get(key)
    if (row) { row.version += 1; row.dirty = true }
    this.revision += 1
    this.notify()
  }

  remove(key) { this.resources.delete(key); this.revision += 1; this.notify() }

  setLoading(loaded, total) {
    if (this.loaded === loaded && this.total === total) return
    this.loaded = loaded
    this.total = total
    this.revision += 1
    this.notify()
  }

  beginOperation(label) {
    const operation = { key: `operation:${++this.revision}`, label, status: 'saving' }
    this.operations.add(operation)
    this.notify()
    return () => {
      if (this.operations.delete(operation)) { this.revision += 1; this.notify() }
    }
  }

  state() {
    const resources = Array.from(this.resources.values()).map(row => ({
      key: row.key, label: row.label,
      status: row.error ? 'error' : row.pending ? 'saving' : row.dirty || row.snapshot() !== row.acknowledged ? 'unsaved' : 'saved',
      errorReason: row.error ? row.errorReason : null,
    }))
    resources.push(...this.operations)
    const statuses = resources.map(row => row.status)
    const status = ['error', 'saving', 'unsaved'].find(value => statuses.includes(value)) || (this.loaded < this.total ? 'loading' : 'saved')
    return { status, resources, loaded: this.loaded, total: this.total }
  }

  async save(key, options = {}) {
    const row = this.resources.get(key)
    if (!row) return true
    if (row.pending) { await row.pending; return this.save(key, options) }
    const snapshot = row.snapshot()
    if (!row.dirty && !row.error && snapshot === row.acknowledged) return true
    const version = row.version
    row.error = false
    row.errorReason = null
    // Invoke immediately, but keep rejection inside the acknowledgment boundary.
    row.pending = (async () => {
      try {
        await row.save(snapshot, options)
        row.acknowledged = snapshot
        row.dirty = row.version !== version || row.snapshot() !== snapshot
        return true
      } catch (error) {
        row.error = true
        row.errorReason = error?.saveMessage || 'We couldn’t confirm these changes were saved. Please try again.'
        return false
      }
    })()
    this.notify()
    const success = await row.pending
    row.pending = null
    this.notify()
    return success
  }

  async saveAll() {
    await Promise.all(Array.from(this.resources.keys(), key => this.save(key)))
    return this.state().status === 'saved'
  }

  notify() { this.onChange(this.state()) }
}
